import React, { useState, useEffect, useRef, useMemo } from "react";
import Context from "../../Context";
import { Header } from "../../components/modules";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/base";
import { HiOutlineDocumentSearch } from "react-icons/hi";
import { IoDocumentTextOutline } from "react-icons/io5";
import { FiRefreshCw } from "react-icons/fi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { OrderStatusBadge } from "../../components/modules";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";

const PAGE_SIZE = 500;
const ITEMS_PER_PAGE = 50;

const normalizeOrder = (doc) => {
  const d = doc.data();
  return { ...d, id: doc.id };
};

export const TaxRequest = () => {
  const { configs, db } = Context();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roundDate, setRoundDate] = useState(configs.roundDate);
  const [currentPage, setCurrentPage] = useState(1);
  const unsubRef = useRef(null);

  useEffect(() => {
    getOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundDate]);

  useEffect(() => {
    return () => {
      if (unsubRef.current) unsubRef.current();
    };
  }, []);

  const buildConstraints = () => {
    return [
      where("roundDate", "==", roundDate),
      where("taxReceiptRequested", "==", true),
      orderBy("taxReceiptRequestedAt", "desc"),
      limit(PAGE_SIZE),
    ];
  };

  const getOrders = () => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
    setLoading(true);
    setCurrentPage(1);

    unsubRef.current = onSnapshot(
      query(collection(db, "Orders"), ...buildConstraints()),
      (snapshot) => {
        const data = snapshot.docs.map(normalizeOrder);
        setOrders(data);
        setLoading(false);
      },
      (err) => {
        console.error("taxRequest onSnapshot:", err);
        setLoading(false);
      },
    );
  };

  const totalItems = orders.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE) || 1;
  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return orders.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [orders, currentPage]);

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    if (currentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, "...", totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages
      );
    }
    return pages;
  };

  return (
    <div className="h-fit w-full max-w-[1600px] mx-auto pb-10 min-w-0">
      <Header
        header="Tax Receipt Requests"
        subHeader="คำขอใบกำกับภาษี"
        icon={<IoDocumentTextOutline className="text-blue" />}
      />

      <div className="mt-4 bg-white rounded-md shadow-md border border-gray-200 p-4 flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3 shrink-0">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-gray-700">งวดสลาก</label>
            <select
              className="h-10 min-w-[140px] px-3 bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-none focus:border-blue transition-all cursor-pointer"
              onChange={(e) => setRoundDate(e.target.value)}
              value={roundDate || ""}
            >
              {configs.roundDates.map((date, index) => (
                <option key={index} value={date}>
                  {date}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={loading}
              className="h-10 px-4 flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-md border border-gray-300 shadow-xs transition-all cursor-pointer disabled:opacity-50"
              onClick={() => {
                getOrders();
              }}
            >
              <FiRefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              <span>รีเฟรช</span>
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 bg-white rounded-md shadow-md border border-gray-200 overflow-hidden">
        <div className="flex items-center p-4 border-b border-gray-200 bg-gray-50/60 justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-800">คำขอใบกำกับภาษี</h2>
            <span className="px-2.5 py-0.5 text-xs font-bold bg-gray-200 text-gray-700 rounded-md">
              {orders.length} รายการ
            </span>
          </div>

          {totalPages > 1 && (
            <div>
              <Pagination className="mx-0">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      className={`${
                        currentPage > 1
                          ? "cursor-pointer"
                          : "cursor-not-allowed hover:bg-transparent opacity-45 pointer-events-none"
                      } font-semibold`}
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                    />
                  </PaginationItem>

                  {getPageNumbers().map((p, idx) => (
                    <PaginationItem key={idx}>
                      {p === "..." ? (
                        <span className="px-2 text-gray-400 text-sm">...</span>
                      ) : (
                        <PaginationLink
                          onClick={() => setCurrentPage(p)}
                          isActive={currentPage === p}
                          className="cursor-pointer font-semibold rounded-md"
                        >
                          {p}
                        </PaginationLink>
                      )}
                    </PaginationItem>
                  ))}

                  <PaginationItem>
                    <PaginationNext
                      className={`${
                        currentPage < totalPages
                          ? "cursor-pointer"
                          : "cursor-not-allowed hover:bg-transparent opacity-45 pointer-events-none"
                      } font-semibold`}
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>

        <div className="w-full overflow-x-auto">
          <Table className="w-full">
            <TableHeader className="bg-gray-100/75 border-b border-gray-200">
              <TableRow className="hover:bg-transparent border-none">
                <TableHead className="w-12 text-center font-bold text-gray-700 py-3 whitespace-nowrap">#</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">รหัสคำสั่งซื้อ</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">เลขที่ใบกำกับภาษี</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  ชื่อ-นามสกุล (ผู้ขอ)
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">ที่อยู่</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">เลขบัตรประชาชน</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">ค่าส่ง (บาท)</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">สลิปค่าส่ง</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">วันที่ขอ</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">สถานะออเดอร์</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">จัดการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="h-[80px]">
                  <TableCell colSpan="11">
                    <div className="w-full h-72 flex flex-col justify-center items-center gap-3 text-gray-500">
                      <div className="w-8 h-8 border-4 border-blue border-t-transparent rounded-full animate-spin" />
                      <p className="text-sm font-semibold">กำลังโหลดข้อมูลคำขอใบกำกับภาษี...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow className="h-[80px]">
                  <TableCell
                    colSpan="11"
                    className="h-72 font-semibold text-center text-gray-400"
                  >
                    ไม่พบข้อมูล
                  </TableCell>
                </TableRow>
              ) : (
                paginatedOrders.map((order, index) => (
                  <TaxRequestCell
                    order={order}
                    key={order.id}
                    index={(currentPage - 1) * ITEMS_PER_PAGE + index}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Bottom Pagination Bar */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50/60 flex-wrap gap-3">
            <div className="text-xs font-semibold text-gray-500">
              แสดง {(currentPage - 1) * ITEMS_PER_PAGE + 1} -{" "}
              {Math.min(currentPage * ITEMS_PER_PAGE, orders.length)} จาก{" "}
              {orders.length} รายการ (หน้า {currentPage} จาก {totalPages})
            </div>
            <Pagination className="mx-0 w-auto">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    className={`${
                      currentPage > 1
                        ? "cursor-pointer"
                        : "cursor-not-allowed hover:bg-transparent opacity-45 pointer-events-none"
                    } font-semibold`}
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                  />
                </PaginationItem>

                {getPageNumbers().map((p, idx) => (
                  <PaginationItem key={idx}>
                    {p === "..." ? (
                      <span className="px-2 text-gray-400 text-sm">...</span>
                    ) : (
                      <PaginationLink
                        onClick={() => setCurrentPage(p)}
                        isActive={currentPage === p}
                        className="cursor-pointer font-semibold rounded-md"
                      >
                        {p}
                      </PaginationLink>
                    )}
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    className={`${
                      currentPage < totalPages
                        ? "cursor-pointer"
                        : "cursor-not-allowed hover:bg-transparent opacity-45 pointer-events-none"
                    } font-semibold`}
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
    </div>
  );
};

const TaxRequestCell = ({ order, index }) => {
  const info = order.taxReceiptInfo || {};
  return (
    <TableRow className="h-[80px]">
      <TableCell className="text-center font-semibold">{index + 1}</TableCell>
      <TableCell className="text-center font-semibold font-mono tabular-nums">
        {order.shortId}
      </TableCell>
      <TableCell className="text-center font-semibold font-mono tabular-nums">
        {order.taxNo || "-"}
      </TableCell>
      <TableCell className="text-center font-semibold">
        {`${info.firstName || ""} ${info.lastName || ""}`.trim() || "-"}
      </TableCell>
      <TableCell className="font-semibold max-w-[200px]">
        <p className="line-clamp-3 text-sm">{info.address || "-"}</p>
      </TableCell>
      <TableCell className="text-center font-semibold font-mono tabular-nums">
        {info.nationalId || "-"}
      </TableCell>
      <TableCell className="text-center font-semibold">
        {info.shippingFee?.toLocaleString() ?? "-"}
      </TableCell>
      <TableCell className="text-center font-semibold">
        {info.slipUrl ? (
          <img
            onClick={() => window.open(info.slipUrl, "_blank")}
            src={info.slipUrl}
            style={{
              width: "100%",
              height: "100%",
              maxWidth: "40px",
              maxHeight: "80px",
              cursor: "pointer",
            }}
            className="mx-auto"
            alt="slip"
          />
        ) : (
          "-"
        )}
      </TableCell>
      <TableCell className="text-center font-semibold">
        {order.taxReceiptRequestedAt
          ? new Date(order.taxReceiptRequestedAt).toLocaleDateString("th-TH", {
              year: "numeric",
              month: "numeric",
              day: "numeric",
              hour: "numeric",
              minute: "numeric",
            }) + " น."
          : "-"}
      </TableCell>
      <TableCell className="font-semibold text-center">
        <OrderStatusBadge status={order.status} size="sm" />
      </TableCell>
      <TableCell>
        <div className="flex justify-center gap-3">
          <button
            className="bg-green rounded-md py-2 px-3 text-white font-semibold hover:opacity-75 duration-150"
            onClick={() => window.open(`/orders/${order.id}`, "_blank")}
          >
            <HiOutlineDocumentSearch className="w-4 h-4" />
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
};
